import { setUserId } from '@amplitude/analytics-browser';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { isSessionStorageSupported } from '../lib/helpers';
import {
  AUTH_METHODS,
  PERSONAL_CHECKS_API,
  SESSION_STORAGE_AUTH_METHOD_KEY,
  SESSION_STORAGE_CANDIDATE_IDS_KEY,
  SESSION_STORAGE_JWT_KEY,
  SESSION_STORAGE_TOKEN_KEY,
  SESSION_STORAGE_WORKOS_DOB_PENDING_KEY,
} from '../constants';
import { trackAnalyticsEvent } from './analytics';
import {
  consumeWorkosDobAnalyticsState,
  incrementWorkosDobAttempt,
  markWorkosDobMismatch,
  resetWorkosDobAnalyticsState,
} from './workosDobAnalytics';
import { ANALYTICS_EVENTS, ANALYTICS_PROPERTIES } from '../lib/analytics';
import { sha256 } from '../lib/sha256';
import { nonCandidateLoggedIn, resetPortal } from './reports';

type AppDispatch = ThunkDispatch<unknown, unknown, AnyAction>;

const WORKOS_AUTH_BASE = '/candidate_portal/workos_auth';

/*
 * Action Types
 */

export const WORKOS_MAGIC_CODE_SENDING = 'WORKOS_MAGIC_CODE_SENDING';
export const WORKOS_MAGIC_CODE_SENT = 'WORKOS_MAGIC_CODE_SENT';

export const WORKOS_RESET = 'WORKOS_RESET';
export const WORKOS_VERIFY_REQUEST = 'WORKOS_VERIFY_REQUEST';
export const WORKOS_VERIFY_SUCCESS = 'WORKOS_VERIFY_SUCCESS';
export const WORKOS_VERIFY_FAILURE = 'WORKOS_VERIFY_FAILURE';
export const WORKOS_DOB_REQUIRED = 'WORKOS_DOB_REQUIRED';

/*
 * Action Creators
 */

function clearWorkosAuthStorage() {
  if (!isSessionStorageSupported()) return;

  window.sessionStorage.removeItem(SESSION_STORAGE_JWT_KEY);
  window.sessionStorage.removeItem(SESSION_STORAGE_CANDIDATE_IDS_KEY);
  window.sessionStorage.removeItem(SESSION_STORAGE_AUTH_METHOD_KEY);
  window.sessionStorage.removeItem(SESSION_STORAGE_TOKEN_KEY);
  window.sessionStorage.removeItem(SESSION_STORAGE_WORKOS_DOB_PENDING_KEY);
}

/**
 * Whether a WorkOS DOB challenge is mid-flight. Single source of truth for the
 * pending-flag semantics — imported by WorkOSLogin, WorkOSLoginCallback, and
 * candidateLogin so the check can't drift across call sites.
 */
export function isWorkosDobPending(): boolean {
  return (
    isSessionStorageSupported() &&
    window.sessionStorage.getItem(SESSION_STORAGE_WORKOS_DOB_PENDING_KEY) ===
      'true'
  );
}

export function getWorkosDobAuthMethod(): string {
  const storedAuthMethod = isSessionStorageSupported()
    ? window.sessionStorage.getItem(SESSION_STORAGE_AUTH_METHOD_KEY)
    : null;

  return storedAuthMethod === AUTH_METHODS.WORKOS_GOOGLE
    ? AUTH_METHODS.WORKOS_GOOGLE
    : AUTH_METHODS.WORKOS_EMAIL;
}

/** Reset WorkOS UI step/analytics without touching the persisted auth session. */
export const workosResetUi = () => (dispatch: AppDispatch) => {
  resetWorkosDobAnalyticsState();
  dispatch({ type: WORKOS_RESET });
};

/** Abandon WorkOS login — clears sessionStorage auth keys and resets UI. */
export const workosReset = () => (dispatch: AppDispatch) => {
  resetWorkosDobAnalyticsState();
  clearWorkosAuthStorage();
  dispatch({ type: WORKOS_RESET });
};
export const workosCodeSending = () => ({ type: WORKOS_MAGIC_CODE_SENDING });
export const workosCodeSent = (email: string) => ({
  type: WORKOS_MAGIC_CODE_SENT,
  payload: { email },
});
export const workosVerifyRequest = () => ({ type: WORKOS_VERIFY_REQUEST });
export const workosVerifySuccess = (payload: {
  candidateJwt: string;
  candidateIds: string[];
  authMethod?: string;
}) => ({
  type: WORKOS_VERIFY_SUCCESS,
  payload: {
    ...payload,
    authMethod: payload.authMethod || AUTH_METHODS.WORKOS_EMAIL,
  },
});
export const workosVerifyFailure = (error: string) => ({
  type: WORKOS_VERIFY_FAILURE,
  payload: { error },
});
export const workosDobRequired = (options?: {
  preserveAnalytics?: boolean;
}) => {
  if (!options?.preserveAnalytics) {
    resetWorkosDobAnalyticsState();
  }
  return { type: WORKOS_DOB_REQUIRED };
};

/*
 * Helpers
 */

interface DispatchFailureOptions {
  dispatch: AppDispatch;
  errorKey: string;
  errorType: string;
  body?: any;
  dobAttemptNumber?: number;
  failureEvent?: string;
  authMethod?: string;
}

function dispatchFailure({
  dispatch,
  errorKey,
  errorType,
  body,
  dobAttemptNumber,
  failureEvent = ANALYTICS_EVENTS.CANDIDATE_PORTAL_WORKOS_LOGIN_FAILED,
  authMethod = AUTH_METHODS.WORKOS_EMAIL,
}: DispatchFailureOptions) {
  dispatch(workosVerifyFailure(errorKey));
  const errorCode = body?.errors?.[0]?.code;
  const isDobVerifyFailure = errorType === 'dobVerifyFailed';

  dispatch(
    trackAnalyticsEvent(failureEvent, {
      [ANALYTICS_PROPERTIES.AUTH_METHOD]: authMethod,
      errorType,
      ...(errorCode && { errorCode }),
      ...(dobAttemptNumber != null &&
        dobAttemptNumber > 0 && {
          dob_attempt_number: dobAttemptNumber,
        }),
      ...(isDobVerifyFailure && {
        dob_error_key: errorKey,
      }),
    }),
  );

  if (isDobVerifyFailure) {
    if (errorKey === 'dobMismatch') {
      markWorkosDobMismatch();
    }
    dispatch(
      trackAnalyticsEvent(
        ANALYTICS_EVENTS.CANDIDATE_PORTAL_WORKOS_DOB_VERIFY_FAILED,
        {
          [ANALYTICS_PROPERTIES.AUTH_METHOD]: authMethod,
          dob_error_key: errorKey,
          ...(dobAttemptNumber != null &&
            dobAttemptNumber > 0 && {
              dob_attempt_number: dobAttemptNumber,
            }),
          ...(errorCode && { errorCode }),
        },
      ),
    );
  }
}

function resolveVerifyErrorKey(status: number, body: any): string {
  const errorCode = body?.errors?.[0]?.code;
  if (errorCode === 'invalid_code') return 'invalidCode';
  if (errorCode === 'no_account') return 'noAccount';
  if (status === 401) return 'invalidCode';
  return 'genericError';
}

function resolveDobVerifyErrorKey(status: number, body: any): string {
  const errorCode = body?.errors?.[0]?.code;
  if (errorCode === 'dob_mismatch') return 'dobMismatch';
  if (status === 401) return 'dobSessionExpired';
  if (status === 429) return 'dobRateLimited';
  if (status === 403 && body?.profile_deleted === true)
    return 'dobProfileDeleted';
  return 'genericError';
}

interface ProcessAuthOptions {
  failureEvent?: string;
  authMethod?: string;
  // Set by verifyWorkOSDob — tags the login success event with dob_challenge:true
  // so analytics can distinguish DOB-gated logins from direct logins.
  fromDobChallenge?: boolean;
  dobAttemptNumber?: number;
}

function processAuthResponse(
  dispatch: AppDispatch,
  res: Response,
  body: any,
  errorType: string,
  getErrorKey: (status: number, body: any) => string = () => 'genericError',
  options: ProcessAuthOptions = {},
): boolean {
  const {
    failureEvent = ANALYTICS_EVENTS.CANDIDATE_PORTAL_WORKOS_LOGIN_FAILED,
    authMethod = AUTH_METHODS.WORKOS_EMAIL,
    fromDobChallenge = false,
    dobAttemptNumber,
  } = options;

  if (!res.ok) {
    dispatchFailure({
      dispatch,
      errorKey: getErrorKey(res.status, body),
      body,
      dobAttemptNumber,
      errorType,
      failureEvent,
      authMethod,
    });
    return false;
  }

  const { token, candidate_ids, dob_required } = body;

  if (dob_required) {
    // Clear the whole auth session, then re-add only the two keys this branch
    // needs. Keeps the key lifecycle in one place (clearWorkosAuthStorage) so a
    // new auth key doesn't have to be mirrored here too.
    clearWorkosAuthStorage();
    if (isSessionStorageSupported()) {
      window.sessionStorage.setItem(
        SESSION_STORAGE_AUTH_METHOD_KEY,
        authMethod,
      );
      window.sessionStorage.setItem(
        SESSION_STORAGE_WORKOS_DOB_PENDING_KEY,
        'true',
      );
    }
    dispatch(workosDobRequired());
    // Returns true because the HTTP call succeeded and state was updated.
    // No caller currently uses the return value to determine auth completion.
    return true;
  }

  // Non-candidate: authenticated but no candidate records. Treat identically
  // regardless of whether the API returned a token (it's useless without IDs).
  if (Array.isArray(candidate_ids) && candidate_ids.length === 0) {
    if (isSessionStorageSupported()) {
      window.sessionStorage.removeItem(SESSION_STORAGE_JWT_KEY);
      window.sessionStorage.removeItem(SESSION_STORAGE_WORKOS_DOB_PENDING_KEY);
      window.sessionStorage.setItem(
        SESSION_STORAGE_AUTH_METHOD_KEY,
        authMethod,
      );
      window.sessionStorage.setItem(SESSION_STORAGE_CANDIDATE_IDS_KEY, '');
    }
    dispatch(resetPortal());
    dispatch(
      workosVerifySuccess({
        candidateJwt: '',
        candidateIds: [],
        authMethod,
      }),
    );
    dispatch(nonCandidateLoggedIn());
    dispatch(
      trackAnalyticsEvent(ANALYTICS_EVENTS.CANDIDATE_PORTAL_LOGIN_SUCCESS, {
        [ANALYTICS_PROPERTIES.AUTH_METHOD]: authMethod,
        [ANALYTICS_PROPERTIES.IS_CANDIDATE]: false,
        ...(fromDobChallenge ? consumeWorkosDobAnalyticsState() : undefined),
      }),
    );
    return true;
  }

  if (!token) {
    const failureType = body.parseError ? 'jsonParseError' : 'missingToken';
    dispatchFailure({
      dispatch,
      errorKey: getErrorKey(res.status, body),
      body,
      dobAttemptNumber,
      errorType: failureType,
      failureEvent,
      authMethod,
    });
    return false;
  }

  if (isSessionStorageSupported()) {
    window.sessionStorage.removeItem(SESSION_STORAGE_WORKOS_DOB_PENDING_KEY);
    window.sessionStorage.setItem(SESSION_STORAGE_JWT_KEY, token);
    window.sessionStorage.setItem(
      SESSION_STORAGE_CANDIDATE_IDS_KEY,
      Array.isArray(candidate_ids) ? candidate_ids.join(',') : '',
    );
    // Keep the auth method with the active JWT so session restoration and
    // analytics retain the login source. workosReset clears both together.
    window.sessionStorage.setItem(SESSION_STORAGE_AUTH_METHOD_KEY, authMethod);
  }

  dispatch(resetPortal());

  dispatch(
    workosVerifySuccess({
      candidateJwt: token,
      candidateIds: candidate_ids || [],
      authMethod,
    }),
  );
  dispatch(
    trackAnalyticsEvent(ANALYTICS_EVENTS.CANDIDATE_PORTAL_LOGIN_SUCCESS, {
      [ANALYTICS_PROPERTIES.AUTH_METHOD]: authMethod,
      [ANALYTICS_PROPERTIES.IS_CANDIDATE]:
        Array.isArray(candidate_ids) && candidate_ids.length > 0,
      ...(fromDobChallenge ? consumeWorkosDobAnalyticsState() : undefined),
    }),
  );
  return true;
}

/*
 * Async Actions
 */

export const sendWorkOSMagicCode =
  (email: string) => async (dispatch: AppDispatch) => {
    dispatch(workosCodeSending());
    dispatch(
      trackAnalyticsEvent(
        ANALYTICS_EVENTS.CANDIDATE_PORTAL_WORKOS_LOGIN_SUBMITTED,
        { [ANALYTICS_PROPERTIES.AUTH_METHOD]: AUTH_METHODS.WORKOS_EMAIL },
      ),
    );

    try {
      const url = new URL(
        `${WORKOS_AUTH_BASE}/magic_auth`,
        PERSONAL_CHECKS_API,
      );
      await fetch(url.toString(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email }),
      });

      // Always transition to code step regardless of response status
      // to prevent email enumeration
      dispatch(workosCodeSent(email));
    } catch {
      // Even on network error, transition to code step
      dispatch(workosCodeSent(email));
    }
  };

export const verifyWorkOSMagicCode =
  (email: string, code: string) => async (dispatch: AppDispatch) => {
    dispatch(workosVerifyRequest());

    try {
      const payload = { email, code };

      const url = new URL(`${WORKOS_AUTH_BASE}/verify`, PERSONAL_CHECKS_API);
      const res = await fetch(url.toString(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      let body: any;
      try {
        body = await res.json();
      } catch {
        body = { parseError: true };
      }
      try {
        setUserId(await sha256(email));
      } catch {
        // Analytics must never break the login flow
      }
      processAuthResponse(
        dispatch,
        res,
        body,
        'verifyFailed',
        resolveVerifyErrorKey,
      );
    } catch {
      dispatchFailure({
        dispatch,
        errorKey: 'genericError',
        errorType: 'networkError',
      });
    }
  };

export const initiateWorkOSGoogleAuth = () => (dispatch: AppDispatch) => {
  if (isSessionStorageSupported()) {
    window.sessionStorage.removeItem(SESSION_STORAGE_WORKOS_DOB_PENDING_KEY);
  }
  dispatch(
    trackAnalyticsEvent(
      ANALYTICS_EVENTS.CANDIDATE_PORTAL_WORKOS_GOOGLE_OAUTH_INITIATED,
      { [ANALYTICS_PROPERTIES.AUTH_METHOD]: AUTH_METHODS.WORKOS_GOOGLE },
    ),
  );

  const redirectUri = `${window.location.origin}/login/email/callback`;
  const url = new URL(
    `${WORKOS_AUTH_BASE}/oauth/authorize`,
    PERSONAL_CHECKS_API,
  );
  url.searchParams.set('redirect_uri', redirectUri);
  window.location.href = url.toString();
};

export const exchangeWorkOSOAuthCode =
  (code: string) => async (dispatch: AppDispatch) => {
    dispatch(workosVerifyRequest());

    try {
      const url = new URL(
        `${WORKOS_AUTH_BASE}/oauth/exchange`,
        PERSONAL_CHECKS_API,
      );
      const res = await fetch(url.toString(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code }),
      });

      let body: any;
      try {
        body = await res.json();
      } catch {
        body = { parseError: true };
      }
      processAuthResponse(
        dispatch,
        res,
        body,
        'exchange_failed',
        () => 'oauthError',
        {
          failureEvent:
            ANALYTICS_EVENTS.CANDIDATE_PORTAL_WORKOS_GOOGLE_OAUTH_FAILED,
          authMethod: AUTH_METHODS.WORKOS_GOOGLE,
        },
      );
    } catch {
      dispatchFailure({
        dispatch,
        errorKey: 'oauthError',
        errorType: 'network_error',
        failureEvent:
          ANALYTICS_EVENTS.CANDIDATE_PORTAL_WORKOS_GOOGLE_OAUTH_FAILED,
        authMethod: AUTH_METHODS.WORKOS_GOOGLE,
      });
    }
  };

export const verifyWorkOSDob =
  (dob: string) => async (dispatch: AppDispatch) => {
    // workosVerifyRequest sets processing:true; processAuthResponse and
    // dispatchFailure both dispatch actions that reset it to false.
    const attemptNumber = incrementWorkosDobAttempt();
    const authMethod = getWorkosDobAuthMethod();
    dispatch(workosVerifyRequest());
    dispatch(
      trackAnalyticsEvent(
        ANALYTICS_EVENTS.CANDIDATE_PORTAL_WORKOS_DOB_SUBMITTED,
        {
          [ANALYTICS_PROPERTIES.AUTH_METHOD]: authMethod,
          ...(attemptNumber > 0 && { dob_attempt_number: attemptNumber }),
          dob_is_retry: attemptNumber > 1,
        },
      ),
    );

    try {
      const url = new URL(
        `${WORKOS_AUTH_BASE}/verify_dob`,
        PERSONAL_CHECKS_API,
      );
      const res = await fetch(url.toString(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ dob }),
      });

      let body: any;
      try {
        body = await res.json();
      } catch {
        body = { parseError: true };
      }
      if (res.status === 401 && isSessionStorageSupported()) {
        window.sessionStorage.removeItem(
          SESSION_STORAGE_WORKOS_DOB_PENDING_KEY,
        );
      }
      processAuthResponse(
        dispatch,
        res,
        body,
        'dobVerifyFailed',
        resolveDobVerifyErrorKey,
        {
          fromDobChallenge: true,
          authMethod,
          dobAttemptNumber: attemptNumber,
        },
      );
    } catch {
      dispatchFailure({
        dispatch,
        errorKey: 'genericError',
        errorType: 'networkError',
        dobAttemptNumber: attemptNumber,
      });
    }
  };

export const workosLogout = () => async (_dispatch: AppDispatch) => {
  try {
    const url = new URL(`${WORKOS_AUTH_BASE}/session`, PERSONAL_CHECKS_API);
    await fetch(url.toString(), {
      method: 'DELETE',
      credentials: 'include',
    });
  } catch {
    // Best-effort session revocation
  }
};
